import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { FormControl, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isValid, parseISO, format } from "date-fns";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// helpers
import { hoursUpdate } from "../../../components/AppTimeComponent/helpers/updateHours";
import { navigateBackOrToMain } from "../../../common/utils/helpers/navigateBackOrToMain";
import { updateMinutes } from "../../../components/AppTimeComponent/helpers/updateMinutes";
import { useDidMountEffect } from "../../../common/hooks/useDidMountEffect";
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../common/utils/transport/airlineTransportContext";
import { parseAirlineDepartureTime } from "../../../common/utils/helpers/parseAirlineDepartureTime";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
import { useCookies } from "react-cookie";
// icons
import { dateIcon, serviceClassIcon, timeIcon } from "../../../common/vector";
// components
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import AppTimeComponent from "../../../components/AppTimeComponent";
import CustomGoogleAutocomplete from "../../../components/CustomGoogleAutocomplete";
import AppButton from "../../../components/ui/AppButton";
import Limos4OfferButtons from "./Limos4OfferButtons";
import Limos4Summary from "./Limos4Summary";
import PaymentModal from "../../../components/Payment/PaymentModal";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { limos4ActionCreators } from "./limos4Module";
import { googleSearchCreators } from "../../../common/store/modules/googleSearchModule";
import { errorFieldActionCreators } from "../../../common/store/modules/errorFieldModule";

interface GooglePlaceResult {
  value?: {
    structured_formatting?: {
      secondary_text?: string;
      main_text?: string;
    };
    place_id?: string;
  };
}

export default function Limos4() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [cookies] = useCookies(["cookieAccepted"]);
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();

  const language = localStorage.getItem("i18nextLng") || "en";

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);

  const transportContext = useMemo(
    () => resolveTransportAirlineApiContext(restoredNavState ?? location.state),
    [restoredNavState, location.state]
  );

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const googlePlacesApiKey = useSelector((state: any) => state.global.googlePlacesApiKey);
  const isLoadingGeocodingDeparture = useSelector((state: any) => state.googleSearch.isLoadingGeocodingDeparture);
  const isLoadingGeocodingDestination = useSelector((state: any) => state.googleSearch.isLoadingGeocodingDestination);
  const geocodingDeparture = useSelector((state: any) => state.googleSearch.geocodingDeparture);
  const geocodingDestination = useSelector((state: any) => state.googleSearch.geocodingDestination);
  const isValidDepartureAddress = useSelector((state: any) => state.googleSearch.isValidDepartureAddress);
  const isValidDestinationAddress = useSelector((state: any) => state.googleSearch.isValidDestinationAddress);
  const limos4Initial = useSelector((state: any) => state.limos4.limos4Initial);
  const limos4SelectedOffer = useSelector((state: any) => state.limos4.limos4SelectedOffer);
  const estimationAvailable = useSelector((state: any) => state.limos4.estimationAvailable);
  const limos4EstimationError = useSelector((state: any) => state.limos4.limos4EstimationError);
  const showLimos4PaymentModal = useSelector((state: any) => state.limos4.showPaymentModal);
  const limos4BookingResponseData = useSelector((state: any) => state.limos4.bookingResponseData);

  // actions
  const getLimos4Initial = useActions(limos4ActionCreators?.getLimos4InitialAction, []);
  const getLimos4Estimation = useActions(limos4ActionCreators?.getLimos4EstimationAction, []);
  const setLimos4SelectedOffer = useActions(limos4ActionCreators?.setLimos4SelectedOfferAction, []);
  const toggleEstimationAvailableState = useActions(limos4ActionCreators?.toggleEstimationAvailableStateAction, []);
  const buyLimos4Ticket = useActions(limos4ActionCreators?.buyTicketAction, []);
  const toggleLimos4PaymentModal = useActions(limos4ActionCreators?.togglePaymentModalAction, []);
  const resetLimos4State = useActions(limos4ActionCreators?.resetLimos4StateAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);
  const getGoogleGeocoding = useActions(googleSearchCreators?.getGoogleGeocoding, []);
  const setIsValidGoogleFromToAddress = useActions(googleSearchCreators?.setIsValidGoogleFromToAddress, []);
  const resetGoogleGeocodingFromToState = useActions(googleSearchCreators?.resetGoogleGeocodingFromToState, []);

  const lastInitialFetchKeyRef = useRef<string | null>(null);

  const initialTime = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 5);
    return d;
  }, []);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [departureHours, setDepartureHours] = useState<number>(initialTime.getHours());
  const [departureMinutes, setDepartureMinutes] = useState<number>(initialTime.getMinutes());
  // Route (Google Places)
  const [selectedFromStation, setSelectedFromStation] = useState<GooglePlaceResult | null>(null);
  const [selectedToStation, setSelectedToStation] = useState<GooglePlaceResult | null>(null);
  // Dropdowns (Europcar-style)
  const [passengers, setPassengers] = useState<number>(1);
  const [luggage, setLuggage] = useState<number>(1);

  const numberOptions = useMemo(() => Array.from({ length: 9 }, (_, i) => i), []);
  const limoOffers = useMemo(() => limos4Initial?.items ?? [], [limos4Initial]);
  const limoOfferButtons = useMemo(
    () =>
      limoOffers.map((offer: { code?: string; name?: string }) => ({
        code: offer?.code ?? "",
        label: offer?.name ?? ""
      })),
    [limoOffers]
  );
  const disabledStyle = useMemo(
    () => (estimationAvailable ? { opacity: 0.4, pointerEvents: "none" } : {}),
    [estimationAvailable]
  );

  // Airline override (Limos4-specific): departure date + time + route (arrival airport -> final destination)
  const airlineNav = useMemo(() => {
    const nav = (restoredNavState ?? location.state) as
      | {
          meta?: { source?: string };
          shared?: {
            departureDate?: string;
            time?: string;
            arrivalDestination?: {
              placeId?: string | null;
              formattedAddress?: { main_text?: string | null; secondary_text?: string | null };
            } | null;
            finalDestination?: {
              placeId?: string | null;
              formattedAddress?: { main_text?: string | null; secondary_text?: string | null };
            } | null;
          };
        }
      | null
      | undefined;

    if (nav?.meta?.source !== "airline") {
      return {
        isAirlineSource: false,
        departureDate: undefined as string | undefined,
        time: undefined as string | undefined,
        arrivalDestination: null as any,
        finalDestination: null as any
      };
    }

    return {
      isAirlineSource: true,
      departureDate: nav.shared?.departureDate,
      time: nav.shared?.time,
      arrivalDestination: nav.shared?.arrivalDestination ?? null,
      finalDestination: nav.shared?.finalDestination ?? null
    };
  }, [location.state, restoredNavState]);

  useEffect(() => {
    if (!airlineNav.isAirlineSource) return;

    if (airlineNav.departureDate) {
      const parsed = parseISO(airlineNav.departureDate);
      if (isValid(parsed)) {
        setSelectedDate(parsed);
      }
    }

    const parsedTime = parseAirlineDepartureTime(airlineNav.time);
    if (parsedTime) {
      setDepartureHours(parsedTime.hours);
      setDepartureMinutes(parsedTime.minutes);
    }
  }, [airlineNav]);

  useEffect(() => {
    if (!airlineNav.isAirlineSource) return;

    const fromPlaceId = airlineNav.arrivalDestination?.placeId ?? null;
    const fromMainText = airlineNav.arrivalDestination?.formattedAddress?.main_text ?? "";
    const fromSecondaryText = airlineNav.arrivalDestination?.formattedAddress?.secondary_text ?? null;

    const toPlaceId = airlineNav.finalDestination?.placeId ?? null;
    const toMainText = airlineNav.finalDestination?.formattedAddress?.main_text ?? "";
    const toSecondaryText = airlineNav.finalDestination?.formattedAddress?.secondary_text ?? null;

    // Only override when we have the data required to render meaningful values.
    if (fromPlaceId && fromSecondaryText) {
      setSelectedFromStation({
        value: {
          structured_formatting: {
            main_text: fromMainText || "",
            secondary_text: fromSecondaryText
          },
          place_id: fromPlaceId
        }
      });
      setIsValidGoogleFromToAddress("departure", true);
    }

    if (toPlaceId && toSecondaryText) {
      setSelectedToStation({
        value: {
          structured_formatting: {
            main_text: toMainText || "",
            secondary_text: toSecondaryText
          },
          place_id: toPlaceId
        }
      });
      setIsValidGoogleFromToAddress("destination", true);
    }
  }, [airlineNav, setIsValidGoogleFromToAddress]);

  useEffect(() => {
    // Reset on unmount (match AirportTaxi pattern: always re-enter fresh)
    return () => {
      toggleLimos4PaymentModal(false);
      resetGoogleGeocodingFromToState();
      resetLimos4State();
      resetErrorField();
      clearPersistedAirlineRouterState();
    };
  }, [toggleLimos4PaymentModal, resetGoogleGeocodingFromToState, resetLimos4State, resetErrorField]);

  useDidMountEffect(() => {
    const isValidAddress = geocodingDeparture?.value?.isValidAddress;
    setIsValidGoogleFromToAddress("departure", isValidAddress);
    setSelectedFromStation(geocodingDeparture);
  }, [geocodingDeparture]);

  useDidMountEffect(() => {
    const isValidAddress = geocodingDestination?.value?.isValidAddress;
    setIsValidGoogleFromToAddress("destination", isValidAddress);
    setSelectedToStation(geocodingDestination);
  }, [geocodingDestination]);

  const handleGeocoding = useCallback(
    (address: any, route: "departure" | "destination") => {
      const shouldUseLatLngValidation = true;
      getGoogleGeocoding(address?.value?.place_id, route, shouldUseLatLngValidation);
    },
    [getGoogleGeocoding]
  );

  const handleDepartureAddress = useCallback(
    (address: any, action: any) => {
      if (action.action === "clear") return;
      handleGeocoding(address, "departure");
    },
    [handleGeocoding]
  );

  const handleDestinationAddress = useCallback(
    (address: any, action: any) => {
      if (action.action === "clear") return;
      handleGeocoding(address, "destination");
    },
    [handleGeocoding]
  );

  const onClearRouteInput = useCallback(
    (input: any) => {
      const isDepartureInput = input === "first_input";
      if (isDepartureInput) {
        setIsValidGoogleFromToAddress("departure", false);
        setSelectedFromStation(null);
      } else {
        setIsValidGoogleFromToAddress("destination", false);
        setSelectedToStation(null);
      }
    },
    [setIsValidGoogleFromToAddress]
  );

  const handleCalculatePrice = useCallback(() => {
    if (!limos4SelectedOffer?.code) return;

    const date = format(new Date(selectedDate), "yyyy-MM-dd");
    const time = `${String(departureHours).padStart(2, "0")}:${String(departureMinutes).padStart(2, "0")}`;

    const payload = {
      time,
      date,
      departureAddress: selectedFromStation?.value?.structured_formatting?.secondary_text ?? null,
      departureAddressId: selectedFromStation?.value?.place_id ?? null,
      destinationAddress: selectedToStation?.value?.structured_formatting?.secondary_text ?? null,
      destinationAddressId: selectedToStation?.value?.place_id ?? null,
      attributes: [],
      details: {
        luggageCount: luggage,
        passengerCount: passengers
      },
      offerCode: limos4SelectedOffer?.code
    };

    getLimos4Estimation(payload, limos4SelectedOffer?.code ?? null, transportContext);
  }, [
    selectedDate,
    departureHours,
    departureMinutes,
    selectedFromStation,
    selectedToStation,
    passengers,
    luggage,
    getLimos4Estimation,
    limos4SelectedOffer,
    transportContext
  ]);

  const handleSelectOffer = useCallback(
    (offerCode: string) => {
      const offer = limoOffers.find((item: { code?: string }) => item?.code === offerCode);
      if (!offer || offer?.code === limos4SelectedOffer?.code) return;

      setLimos4SelectedOffer(offer);
      toggleEstimationAvailableState(false);
    },
    [limoOffers, limos4SelectedOffer, setLimos4SelectedOffer, toggleEstimationAvailableState]
  );

  const handleEditTicket = useCallback(() => {
    toggleEstimationAvailableState(false);
  }, [toggleEstimationAvailableState]);

  const handleBook = useCallback(() => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: limos4SelectedOffer?.code ?? null
    });

    buyLimos4Ticket(
      {
        start_date: Math.round(+new Date(selectedDate) / 1000),
        timeslot: `${departureHours}:${departureMinutes}`,
        offer_code: limos4SelectedOffer?.code ?? null,
        // keep extra booking data consistent with other transports
        departureAddress: selectedFromStation?.value?.structured_formatting?.secondary_text,
        departureAddressId: selectedFromStation?.value?.place_id ?? null,
        destinationAddress: selectedToStation?.value?.structured_formatting?.secondary_text,
        destinationAddressId: selectedToStation?.value?.place_id ?? null,
        details: { passengerCount: passengers, luggageCount: luggage }
      },
      navigate,
      transportContext
    );
  }, [
    buyLimos4Ticket,
    cookies.cookieAccepted,
    platformType,
    tenantInfo,
    selectedDate,
    departureHours,
    departureMinutes,
    passengers,
    luggage,
    limos4SelectedOffer,
    selectedFromStation,
    selectedToStation,
    navigate,
    transportContext
  ]);

  useEffect(() => {
    if (!tenantInfo) return;

    const fetchKey =
      transportContext.mode === "airline"
        ? `airline:${transportContext.iataCode}`
        : transportContext.mode === "airline_missing_iata"
        ? "airline:missing_iata"
        : "hotel";

    if (lastInitialFetchKeyRef.current === fetchKey) return;
    lastInitialFetchKeyRef.current = fetchKey;

    getLimos4Initial(transportContext);
  }, [tenantInfo, getLimos4Initial, transportContext]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: "/transport/limos4",
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: transportContext.mode === "airline" ? transportContext.iataCode : null,
    onProviderLanguageChange: () => getLimos4Initial(transportContext)
  });

  return (
    <Grid
      className="limos4 grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      <TransportProviderHeader
        title={limos4Initial?.provider_title ?? limos4Initial?.title}
        fallbackTitle="LIMOS4"
        titleClassName="limos4__title"
        shortDescription={limos4Initial?.provider_description}
        providerLogo={limos4Initial?.provider_logo}
      />

      <Grid container>
        {/* LEFT SIDE */}
        <Grid
          item
          md={6}
          xs={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            ...disabledStyle
          }}
        >
          <Box className="row-action" pb={{ xs: 5, sm: 3 }} pr={2}>
            <Grid
              item
              sm={4}
              xs={5}
              sx={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center", alignItems: "center" }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <img src={dateIcon?.default} alt="date" />
                <Box pt={2} sx={{ textAlign: "center", fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.departureDate")?.toUpperCase()}
                </Box>
              </Box>
            </Grid>
            <Grid item sm={8} xs={7} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppDatePickerCustom
                    selectedDate={selectedDate}
                    setSelectedDate={date => setSelectedDate(date ?? new Date())}
                    minDate={new Date()}
                    dataAttribute="limos4-date"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Box className="row-action" pb={{ xs: 5, sm: 3 }} pr={2}>
            <Grid
              item
              sm={4}
              xs={5}
              sx={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center", alignItems: "center" }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <img src={timeIcon?.default} alt="time" />
                <Box pt={2} sx={{ textAlign: "center", fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.departureTime")?.toUpperCase()}
                </Box>
              </Box>
            </Grid>
            <Grid item sm={8} xs={7} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                  <AppTimeComponent
                    hours={departureHours}
                    minutes={departureMinutes}
                    handleHoursChange={(change: string, amount: number) =>
                      hoursUpdate(setDepartureHours, change, amount, departureHours)
                    }
                    handleMinutesChange={(change: string, amount: number) =>
                      updateMinutes(
                        setDepartureMinutes,
                        setDepartureHours,
                        change,
                        amount,
                        departureHours,
                        departureMinutes
                      )
                    }
                    timeBoxSize="w-80"
                    dataCyAttribute="limos4-time"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {limoOfferButtons.length > 0 && (
            <Box className="row-action" pb={{ xs: 5, sm: 3 }} pr={2}>
              <Grid
                item
                sm={4}
                xs={5}
                sx={{
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <img src={serviceClassIcon?.default} alt="type of limo" />
                  <Box pt={2} sx={{ textAlign: "center", fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.typeOfLimo")?.toUpperCase()}
                  </Box>
                </Box>
              </Grid>
              <Grid item sm={8} xs={7} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                  <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                    <Limos4OfferButtons
                      options={limoOfferButtons}
                      selectedCode={limos4SelectedOffer?.code}
                      onSelect={handleSelectOffer}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>

        {/* RIGHT SIDE */}
        <Grid item md={6} xs={12} mt={{ sm: 6, xs: 6, md: 0 }} position={"relative"}>
          <div className="vertical-divider" />
          <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }} sx={{ ...disabledStyle }}>
            {/* ROUTE */}
            {googlePlacesApiKey && (
              <CustomGoogleAutocomplete
                apiKey={googlePlacesApiKey}
                inputTestIdOfFirstInput="limos4-departure"
                inputTestIdOfSecondInput="limos4-destination"
                valueOfFirstInput={selectedFromStation}
                valueOfSecondInput={selectedToStation}
                setValueOfFirstInput={handleDepartureAddress}
                setValueOfSecondInput={handleDestinationAddress}
                onClear={onClearRouteInput}
                isLoadingFirstInput={isLoadingGeocodingDeparture}
                isLoadingSecondInput={isLoadingGeocodingDestination}
                showStar={true}
                location={{ lat: 0, lng: 0 }}
                isEditableFirstInput={false}
                dataCyAttribute="limos4-route"
              />
            )}

            {/* DROPDOWNS */}
            <Box
              sx={{ textTransform: "uppercase", display: "flex", gap: 2 }}
              mt={2}
              flexWrap={{ xs: "wrap", sm: "nowrap" }}
            >
              <Box sx={{ flex: 1, minWidth: 206 }}>
                <Box pb={1} sx={{ textAlign: "left", fontSize: { xs: 12, sm: 13 } }}>
                  {t("transport.numberOfPassengers")}
                </Box>
                <FormControl fullWidth>
                  <Select
                    value={passengers}
                    onChange={e => setPassengers(Number(e.target.value))}
                    input={<OutlinedInput data-cy="limos4-passengers-input" />}
                    renderValue={value => String(value)}
                    data-cy="passengers"
                  >
                    {numberOptions.map(n => (
                      <MenuItem key={n} value={n} data-cy={`limos4-passengers-${n}`}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: 206 }}>
                <Box pb={1} sx={{ textAlign: "left", fontSize: { xs: 12, sm: 14 } }}>
                  {t("transport.piecesOfLuggage")}
                </Box>
                <FormControl fullWidth>
                  <Select
                    value={luggage}
                    onChange={e => setLuggage(Number(e.target.value))}
                    input={<OutlinedInput data-cy="limos4-luggage-input" />}
                    renderValue={value => String(value)}
                    data-cy="limos4-luggage"
                  >
                    {numberOptions.map(n => (
                      <MenuItem key={n} value={n} data-cy={`limos4-luggage-${n}`}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* CTA */}
            <Grid
              container={airlineNav.isAirlineSource}
              item={!airlineNav.isAirlineSource}
              xs={12}
              sm={airlineNav.isAirlineSource ? undefined : 4}
              sx={{
                mt: 3,
                ...(airlineNav.isAirlineSource ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
              }}
            >
              {airlineNav.isAirlineSource && (
                <Grid item sx={{ flex: 1 }}>
                  <AppButton
                    label={t("transport.goBack")}
                    onClick={() => navigateBackOrToMain(navigate, { language })}
                    disableRipple={true}
                    customBackgroundColor={theme.palette.primary.dark}
                    customHoverColor={theme.palette.primary.dark}
                    fontSize="14px"
                    dataAttribute="limos4-go-back-1"
                  />
                </Grid>
              )}

              <Grid
                item
                sx={{
                  opacity: isValidDepartureAddress && isValidDestinationAddress ? 1 : 0.8,
                  ...(airlineNav.isAirlineSource ? { flex: 1 } : {})
                }}
              >
                <AppButton
                  label={t("transport.calculatePriceBtn")}
                  onClick={handleCalculatePrice}
                  disableRipple={true}
                  customBackgroundColor={tenantInfo?.style?.button?.color}
                  customActiveColor={tenantInfo?.style?.button?.active}
                  customHoverColor={tenantInfo?.style?.button?.hover}
                  customClickColor={tenantInfo?.style?.button?.click}
                  fontSize="14px"
                  disabled={!(isValidDepartureAddress && isValidDestinationAddress && limos4SelectedOffer?.code)}
                  dataAttribute="limos4-calculate"
                />
              </Grid>
            </Grid>
            {limos4EstimationError && (
              <Box mt={1} className="limos4__error-msg">
                {t("transport.prebookCustomError")}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
        {estimationAvailable && (
          <Limos4Summary
            onBookBtn={handleBook}
            onEditTicket={handleEditTicket}
            selectedFromStation={selectedFromStation}
            selectedToStation={selectedToStation}
            cancellationPolicy={limos4SelectedOffer?.cancellation_policy || limos4Initial?.cancellation_policy}
          />
        )}
      </Grid>

      {showLimos4PaymentModal && (
        <PaymentModal
          isOpen={showLimos4PaymentModal}
          bookingResponseData={limos4BookingResponseData}
          togglePaymentModal={toggleLimos4PaymentModal}
          redirectUrl="transport/limos4/confirmation"
        />
      )}
    </Grid>
  );
}
